import React, { useState } from "react";
import ReactFlow, {Controls, Node, Edge } from "reactflow";
import "reactflow/dist/style.css";

import { nodeTypes } from "./CampaignFlow/nodeTypes";
import { edgeTypes } from "components/Flow/Edges/edgeTypes";
import useUpdatedLocalNodeValues from "components/Flow/Nodes/useUpdatedLocalNodeValues";
import useUpdatedLocalEdgeValues from "components/Flow/Edges/useUpdatedLocalEdgeValues";
import AddNodeButton from "components/Flow/Controls/AddNodeButton";
import StandardBackground from "components/Flow/StandardBackground";

import { 
  campaignNodeAPI,
  campaignEdgeAPI,
  getCampaignPathwayData,
  nodeTypesAPI,
} from "api/Campaigns/CampaignAPI";

import {
  onNodesChangeHandler,
  onNodeClickHandler,
  onNodeDragStopHandler,
  onPaneClickHandler,
  reloadFlowData,
  useDeleteNodeHotkey,
} from "components/Flow/Nodes/nodeHandlers";

import {
  onConnectHandler,
  onEdgeClickHandler,
} from "components/Flow/Edges/edgeHandlers";

import ControlPanel from "components/Flow/Controls/ControlPanel";
import DeleteConfirmDialog from "components/Flow/Nodes/DeleteConfirmDialog";




interface CampaignFlowProps {
  nodes: Node[];
  edges: Edge[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onNodeSelect: (node: Node | null) => void;
  campaignId: string;
  onNodesReload?: () => Promise<void>;
}

const CampaignFlow: React.FC<CampaignFlowProps> = ({
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodeSelect,
  campaignId,
}) => {

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const updatedNodes = useUpdatedLocalNodeValues(nodes, { selectedNodeId: selectedNode?.id, update: { type: "campaignPathwayNode" } });
  const updatedEdges = useUpdatedLocalEdgeValues(edges, { update: { type: "standardEdge" } });
  
  const onNodesReload = () => reloadFlowData(campaignId, setNodes, setEdges, getCampaignPathwayData);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // triggered by hotkey
  const requestDelete = React.useCallback(() => {
      setDeleteDialogOpen(true);
    }, []);

  const confirmDelete = React.useCallback(async () => {
    if (!selectedNode) return;
    
    // Optimistically remove from UI
    setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
    setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));

    try {
      await campaignNodeAPI.delete(selectedNode.id);
    } catch (err) {
      console.error("Failed to delete node:", err);
    }

    setSelectedNode(null);
    setDeleteDialogOpen(false);
  } , [selectedNode]);

  useDeleteNodeHotkey(selectedNode, requestDelete);

  const cancelDelete = React.useCallback(() => setDeleteDialogOpen(false), []);

  const updateNodeFunctionality = (
    nodeId: string,
    functionalityId: number,
    field: string,
    value: any,
    newValueId?: number
  ) => {
    setNodes(nodes =>
      nodes.map(node =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                functionalities: node.data.functionalities.map((f: any) =>
                  f.id === functionalityId
                    ? { 
                        ...f, 
                        [field]: value,
                        ...(newValueId ? { valueId: newValueId } : {}) // ⭐ THIS IS CRITICAL
                      }
                    : f
                ),
              },
            }
          : node
      )
    );
  };



  const updateNode = (
    nodeId: string,
    frontendKey: string,
    value: any
  ) => {
    setNodes(nodes =>
      nodes.map(node =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                [frontendKey]: value
              },
            }
          : node
      )
    );
  };


  return (
    <div style={{ height: "80vh", width: "100%", position: "relative" }}>
      <ReactFlow
        nodes={updatedNodes.map(n => ({
          ...n,
          data: {
            ...n.data,
            updateNodeFunctionality: n.data.updateNodeFunctionality ?? updateNodeFunctionality,
            updateNode: n.data.updateNode ?? updateNode,
          }
        }))}
        edges={updatedEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={(changes) => onNodesChangeHandler(setNodes, changes)}
        onNodeClick={(_e, node) => {
          setSelectedNode(node);
          onNodeClickHandler(node, onNodeSelect);
        }}
        onPaneClick={() => {
          setSelectedNode(null);
          onNodeSelect(null);
          onPaneClickHandler(onNodeSelect);
        }}
        onNodeDragStop={(_e, node) => onNodeDragStopHandler(node, setNodes, campaignNodeAPI.update)}
        onConnect={(connection) => onConnectHandler(connection, setEdges, nodes, campaignEdgeAPI.create)}
        onEdgeClick={(_e, edge) => onEdgeClickHandler(edge, setEdges, campaignEdgeAPI.delete)}
        fitView
        snapToGrid
        snapGrid={[20, 20]}
        minZoom = {0.1}
        maxZoom = {4}
        deleteKeyCode={null} 
      >
        
        <Controls />
        <StandardBackground />
        <ControlPanel>
          <AddNodeButton
            setNodes={setNodes}
            setEdges={setEdges}
            systemId={campaignId}
            onNodesReload={() => reloadFlowData(campaignId, setNodes, setEdges, getCampaignPathwayData)}
            getAllNodeTypesFn={nodeTypesAPI.getAll}
            createNodeFn={campaignNodeAPI.create}
            getFlowDataFn={getCampaignPathwayData}
            parentKey="campaign"
          />
        </ControlPanel>
      </ReactFlow>
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        warningMessage="Deleting this campaign node will also remove any linked Interactions."
      />
    </div>
  );
};

export default CampaignFlow;
